import type { BuildInfoDto, HealthDto } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { config } from '../config';
import build from '../generated/version.json';
import { formatWhen } from '../time';

/**
 * Copyright and the application version (D-021, D-069). The footer shows the version only; the
 * web and API builds (commit, build time) are in its tooltip, and a mismatch is flagged — the two
 * deploy separately. /api/health is anonymous, so this needs no token.
 */

export function formatBuild(label: string, b: BuildInfoDto): string {
  return `${label} ${b.commit}${b.dirty ? '+dirty' : ''}, built ${formatWhen(b.builtAt)}`;
}

export function Footer() {
  const [api, setApi] = useState<BuildInfoDto | null | 'unreachable'>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.apiBaseUrl}/api/health`, { headers: { Accept: 'application/json' } })
      .then(async (r) => ((await r.json()) as HealthDto).build)
      .then((b) => !cancelled && setApi(b ?? 'unreachable'))
      .catch(() => !cancelled && setApi('unreachable'));
    return () => {
      cancelled = true;
    };
  }, []);

  const web = build as BuildInfoDto;
  const mismatch = api !== null && api !== 'unreachable' && api.commit !== web.commit;
  const detail = [
    formatBuild('Web', web),
    api === null ? 'API …' : api === 'unreachable' ? 'API unreachable' : formatBuild('API', api),
  ].join('\n');

  return (
    <footer className="footer" aria-label="Build information">
      <span>© {new Date().getFullYear()} Weaver Technologies. All rights reserved.</span>
      <span className="footer__build">
        <span title={detail} tabIndex={0}>v{web.version}</span>
        {mismatch && (
          <>
            <span className="footer__sep">·</span>
            <span className="footer__warn">Web and API are different builds</span>
          </>
        )}
      </span>
    </footer>
  );
}
